// client/src/components/POIMarker.js
import React, { useRef, useMemo, useState, useEffect, useCallback } from 'react';
import L from 'leaflet';
import { Marker, Tooltip, useMap } from 'react-leaflet';
import EditPOI from './EditPOI';
import MovePOIModal from './EditPOI_parts/MovePOIModal';

const API_BASE =
  (typeof import.meta !== 'undefined' &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE &&
    String(import.meta.env.VITE_API_BASE).trim()) ||
  '';

function stripTrailingSlash(s) {
  return String(s || '').replace(/\/+$/, '');
}

function stripLeadingSlash(s) {
  return String(s || '').replace(/^\/+/, '');
}

function apiUrl(path) {
  const base = stripTrailingSlash(API_BASE);
  const p = String(path || '');
  if (!base) return p || '';
  if (!p) return base;
  if (p.startsWith('/')) return `${base}${p}`;
  return `${base}/${stripLeadingSlash(p)}`;
}

function resolveUploadsUrl(src) {
  if (!src) return '';
  const trimmed = String(src).trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;

  const base = stripTrailingSlash(API_BASE);

  if (trimmed.startsWith('/')) {
    if (base) return `${base}${trimmed}`;
    return trimmed;
  }

  if (base) return `${base}/${stripLeadingSlash(trimmed)}`;
  return `/${stripLeadingSlash(trimmed)}`;
}

function normalizeHex(c) {
  if (c == null) return '#ff0000';

  if (typeof c === 'number' && Number.isFinite(c)) {
    const n = Math.max(0, Math.min(16777215, Math.round(c)));
    return `#${n.toString(16).padStart(6, '0')}`;
  }

  let s = String(c).trim();
  if (!s) return '#ff0000';

  if (/^\d+$/.test(s)) {
    const n = parseInt(s, 10);
    if (Number.isFinite(n)) {
      const clamped = Math.max(0, Math.min(16777215, Math.round(n)));
      return `#${clamped.toString(16).padStart(6, '0')}`;
    }
    return '#ff0000';
  }

  if (!s.startsWith('#')) s = `#${s}`;

  if (s.length === 4) {
    return '#' + s[1] + s[1] + s[2] + s[2] + s[3] + s[3];
  }

  if (s.length === 7) return s;

  const hex = s.replace(/^#/, '');
  if (/^[0-9a-fA-F]{6}$/.test(hex)) return `#${hex.toLowerCase()}`;

  return '#ff0000';
}

function toBool01(v) {
  if (v === true) return true;
  if (v === false) return false;
  if (v == null) return false;

  if (typeof v === 'number') return Number.isFinite(v) && v === 1;

  const s = String(v).trim().toLowerCase();
  if (!s) return false;

  if (s === '1' || s === 'true' || s === 'yes' || s === 'y' || s === 'on') return true;
  if (s === '0' || s === 'false' || s === 'no' || s === 'n' || s === 'off') return false;

  const n = parseInt(s, 10);
  if (Number.isFinite(n)) return n === 1;

  return false;
}

function isLikelyImageUrl(v) {
  if (v == null) return false;
  const s = String(v).trim();
  if (!s) return false;

  const lower = s.toLowerCase();

  if (
    lower === 'circle' ||
    lower === 'square' ||
    lower === 'triangle' ||
    lower === 'diamond' ||
    lower === 'hex' ||
    lower === 'octagon' ||
    lower === 'pin' ||
    lower === 'flag' ||
    lower === 'crosshair' ||
    lower === 'shield' ||
    lower === 'badge' ||
    lower === 'tag' ||
    lower === 'key' ||
    lower === 'lock' ||
    lower === 'unlock' ||
    lower === 'unlocked' ||
    lower === 'info' ||
    lower === 'plus' ||
    lower === 'bolt' ||
    lower === 'drop' ||
    lower === 'wrench' ||
    lower === 'cone' ||
    lower === 'pill' ||
    lower === 'star' ||
    lower === 'heart' ||
    lower === 'none' ||
    lower === 'null' ||
    lower === 'undefined'
  ) {
    return false;
  }

  if (lower.startsWith('data:image/')) return true;
  if (lower.startsWith('http://') || lower.startsWith('https://')) return true;
  if (lower.startsWith('/')) return true;

  if (/\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(lower)) return true;
  if (lower.includes('/uploads/') || lower.includes('/maps/') || lower.includes('/styles/')) return true;

  return false;
}

function renderTextWithLinks(text) {
  if (!text) return null;

  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/[^\s]*)?)/g;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (!part) return null;

    const isUrl = /^(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/[^\s]*)?)$/i.test(part);
    if (isUrl) {
      const startsWithHttp = part.toLowerCase().startsWith('http://') || part.toLowerCase().startsWith('https://');
      const href = startsWithHttp ? part : `https://${part}`;
      return (
        <a
          key={`link-${index}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: 'underline' }}
        >
          {part}
        </a>
      );
    }

    const lines = part.split('\n');
    return lines.map((line, i) => (
      <React.Fragment key={`text-${index}-${i}`}>
        {line}
        {i < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  });
}

function getColouredIconHTML(iconUrl, colour, size = 64, labelBorder = 1, labelBgColour = '#000', labelOpacity = 1) {
  const showBorder = Number(labelBorder) === 1;
  const col = normalizeHex(colour);

  const bgRaw = (labelBgColour ?? '').toString().trim().toLowerCase();
  const isTransparentBg = !labelBgColour || bgRaw === 'transparent' || bgRaw === 'none';

  const innerHtmlTransparent = `
      <div style="display:flex;flex-direction:column;align-items:center;pointer-events:none;opacity:${labelOpacity};">
        <img src="${iconUrl}" style="width:${size}px;height:${size}px;object-fit:contain;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.22));opacity:${labelOpacity};" />
      </div>
    `;

  if (isTransparentBg) {
    return `
      <div style="transform:scale(var(--poi-label-scale,1));transform-origin:bottom center;">
        ${innerHtmlTransparent}
      </div>
    `;
  }

  const bgStyle = `background:${labelBgColour};box-shadow:0 2px 8px rgba(0,0,0,0.25);`;
  const borderStyle = showBorder ? `border:3px solid ${col};` : 'border:none;';

  const innerHtml = `
    <div style="display:flex;flex-direction:column;align-items:center;pointer-events:none;opacity:${labelOpacity};">
      <div style="width:${size}px;height:${size}px;border-radius:14px;display:flex;align-items:center;justify-content:center;${bgStyle}${borderStyle}font-weight:bold;">
        <img src="${iconUrl}" style="width:${size * 0.75}px;height:${size * 0.75}px;object-fit:contain;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.22));opacity:${labelOpacity};" />
      </div>
    </div>
  `;

  return `
    <div style="transform:scale(var(--poi-label-scale,1));transform-origin:bottom center;">
      ${innerHtml}
    </div>
  `;
}

function getLabelHTML(label, colour = '#fff', labelBorder = 1, labelBgColour = '#000', labelOpacity = 1, showPointer = true) {
  const showBorder = Number(labelBorder) === 1;
  const col = normalizeHex(colour);
  const bgRaw = (labelBgColour ?? '').toString().trim().toLowerCase();
  const isTransparentBg = !labelBgColour || bgRaw === 'transparent' || bgRaw === 'none';

  const mainBgStyle = isTransparentBg
    ? 'background:transparent;box-shadow:none;'
    : `background:${labelBgColour};box-shadow:0 2px 10px #0006;`;

  const borderStyle = isTransparentBg ? 'border:none;' : showBorder ? `border:3px solid ${col};` : 'border:none;';

  const pointerStyle = !showPointer || isTransparentBg ? 'display:none;' : showBorder ? '' : 'display:none;';

  const innerHtml = `
    <div style="${mainBgStyle}color:#fff;border-radius:10px;${borderStyle}font-weight:bold;padding:6px 18px;font-size:21px;display:inline-block;text-align:center;min-width:60px;position:relative;opacity:${labelOpacity};white-space:nowrap;">
      ${label}
      <div style="position:absolute;left:50%;bottom:-15px;transform:translateX(-50%);width:0;height:0;border-left:12px solid transparent;border-right:12px solid transparent;border-top:13px solid ${col};${pointerStyle}"></div>
    </div>
  `;

  return `
    <div style="transform:scale(var(--poi-label-scale,1));transform-origin:bottom center;">
      ${innerHtml}
    </div>
  `;
}

function getIconWithLabelHTML(
  iconUrl,
  label,
  colour = '#fff',
  iconSize = 64,
  labelBorder = 1,
  labelBgColour = '#000',
  labelOpacity = 1
) {
  const showBorder = Number(labelBorder) === 1;
  const col = normalizeHex(colour);

  const bgRaw = (labelBgColour ?? '').toString().trim().toLowerCase();
  const isTransparentBg = !labelBgColour || bgRaw === 'transparent' || bgRaw === 'none';

  const labelMainBgStyle = isTransparentBg
    ? 'background:transparent;box-shadow:none;'
    : `background:${labelBgColour};box-shadow:0 2px 10px #0006;`;

  const labelBorderStyle = isTransparentBg ? 'border:none;' : showBorder ? `border:3px solid ${col};` : 'border:none;';

  const iconBoxBgStyle = isTransparentBg
    ? 'background:transparent;box-shadow:none;'
    : `background:${labelBgColour};box-shadow:0 2px 8px rgba(0,0,0,0.25);`;

  const iconBoxBorderStyle = isTransparentBg ? 'border:none;' : showBorder ? `border:3px solid ${col};` : 'border:none;';

  const innerHtml = `
    <div style="display:flex;flex-direction:column;align-items:center;gap:8px;pointer-events:none;opacity:${labelOpacity};">
      <div style="width:${iconSize}px;height:${iconSize}px;border-radius:14px;display:flex;align-items:center;justify-content:center;${iconBoxBgStyle}${iconBoxBorderStyle}">
        <img src="${iconUrl}" style="width:${iconSize * 0.75}px;height:${iconSize * 0.75}px;object-fit:contain;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.22));opacity:${labelOpacity};" />
      </div>
      <div style="${labelMainBgStyle}color:#fff;border-radius:10px;${labelBorderStyle}font-weight:bold;padding:6px 18px;font-size:21px;display:inline-block;text-align:center;min-width:60px;opacity:${labelOpacity};white-space:nowrap;">
        ${label}
      </div>
    </div>
  `;

  return `
    <div style="transform:scale(var(--poi-label-scale,1));transform-origin:bottom center;">
      ${innerHtml}
    </div>
  `;
}

function normalizeVisualStyle(v) {
  const s0 = String(v ?? '').trim().toLowerCase();
  if (!s0) return 'shape_text';

  const map = {
    shape_label: 'shape_text',
    shape_text: 'shape_text',
    shape_only: 'shape_only',
    auto: 'auto',

    number: 'number',
    number_label: 'number_label',
    number_hover: 'number_label',
    number_mouseover: 'number_label',

    hidden: 'shape_only',
    invisible: 'shape_only',

    label_only: 'text_only',
    icon_only: 'icon_only',
    icon_text: 'icon_text',

    icon_label: 'icon_label',

    icon_label_above: 'icon_label_above',
    icon_label_static: 'icon_label_above',
    icon_label_always: 'icon_label_above',
  };

  return map[s0] || s0;
}

function isShapeOnly(style) {
  const s = normalizeVisualStyle(style);
  return s === 'shape_only' || s === 'path';
}

function isShapeText(style) {
  const s = normalizeVisualStyle(style);
  return s === 'shape_text';
}

function isAuto(style) {
  const s = normalizeVisualStyle(style);
  return s === 'auto';
}

function isNumber(style) {
  const s = normalizeVisualStyle(style);
  return s === 'number';
}

function isNumberLabel(style) {
  const s = normalizeVisualStyle(style);
  return s === 'number_label';
}

function isIconOnly(style) {
  const s = normalizeVisualStyle(style);
  return s === 'icon_only';
}

function isTextOnly(style) {
  const s = normalizeVisualStyle(style);
  return s === 'text_only';
}

function isIconText(style) {
  const s = normalizeVisualStyle(style);
  return s === 'icon_text';
}

function isIconLabel(style) {
  const s = normalizeVisualStyle(style);
  return s === 'icon_label';
}

function isIconLabelAbove(style) {
  const s = normalizeVisualStyle(style);
  return s === 'icon_label_above';
}

const PA_SHAPES = [
  'circle',
  'square',
  'triangle',
  'diamond',
  'hex',
  'octagon',
  'pin',
  'flag',
  'crosshair',
  'shield',
  'badge',
  'tag',
  'key',
  'lock',
  'unlock',
  'unlocked',
  'info',
  'plus',
  'bolt',
  'drop',
  'wrench',
  'cone',
  'pill',
  'star',
  'heart',
];

function normalizeMarkerShape(v) {
  const s = String(v ?? '').trim().toLowerCase();
  if (!s) return '';
  if (s === 'unlocked') return 'unlock';
  if (PA_SHAPES.includes(s)) return s;
  return '';
}

function normalizeBadgeNumberValue(v) {
  const raw = typeof v === 'number' ? v : parseInt(String(v ?? '').trim(), 10);
  if (!Number.isFinite(raw)) return 1;
  return Math.max(1, Math.min(125, Math.round(raw)));
}

function numberValueToBadgeText(n) {
  const nn = normalizeBadgeNumberValue(n);
  if (nn >= 100 && nn <= 125) {
    return String.fromCharCode(65 + (nn - 100));
  }
  return String(Math.max(1, Math.min(99, nn)));
}

function hexToRgb(hex) {
  const h = normalizeHex(hex).replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return { r, g, b };
}

function pickBlackOrWhiteText(hexFill) {
  const { r, g, b } = hexToRgb(hexFill);
  const sr = r / 255;
  const sg = g / 255;
  const sb = b / 255;
  const lin = (v) => (v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4));
  const L = 0.2126 * lin(sr) + 0.7152 * lin(sg) + 0.0722 * lin(sb);
  return L > 0.55 ? '#0b0c0e' : '#ffffff';
}

function getShapeNodeSvg(shape, fill, withOutline = true) {
  const c = normalizeHex(fill);
  const s = normalizeMarkerShape(shape) || 'circle';

  if (s === 'circle') {
    if (!withOutline) return `<circle cx="16" cy="16" r="13" fill="${c}" />`;
    const outerR = 14;
    const innerR = 11;
    return `
      <g>
        <circle cx="16" cy="16" r="${outerR}" fill="none" stroke="#000000" stroke-width="4" />
        <circle cx="16" cy="16" r="${outerR}" fill="none" stroke="#ffffff" stroke-width="2" />
        <circle cx="16" cy="16" r="${innerR}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'square') {
    if (!withOutline) return `<rect x="4" y="4" width="24" height="24" rx="5" ry="5" fill="${c}" />`;
    return `
      <g>
        <rect x="4" y="4" width="24" height="24" rx="5" ry="5" fill="none" stroke="#000000" stroke-width="4" />
        <rect x="4" y="4" width="24" height="24" rx="5" ry="5" fill="none" stroke="#ffffff" stroke-width="2" />
        <rect x="4" y="4" width="24" height="24" rx="5" ry="5" fill="${c}" />
      </g>
    `;
  }

  if (s === 'triangle') {
    const points = '16,4 29,28 3,28';
    if (!withOutline) return `<polygon points="${points}" fill="${c}" />`;
    return `
      <g>
        <polygon points="${points}" fill="none" stroke="#000000" stroke-width="4" />
        <polygon points="${points}" fill="none" stroke="#ffffff" stroke-width="2" />
        <polygon points="${points}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'diamond') {
    const points = '16,3 29,16 16,29 3,16';
    if (!withOutline) return `<polygon points="${points}" fill="${c}" />`;
    return `
      <g>
        <polygon points="${points}" fill="none" stroke="#000000" stroke-width="4" />
        <polygon points="${points}" fill="none" stroke="#ffffff" stroke-width="2" />
        <polygon points="${points}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'hex') {
    const points = '16,3 27,9.5 27,22.5 16,29 5,22.5 5,9.5';
    if (!withOutline) return `<polygon points="${points}" fill="${c}" />`;
    return `
      <g>
        <polygon points="${points}" fill="none" stroke="#000000" stroke-width="4" />
        <polygon points="${points}" fill="none" stroke="#ffffff" stroke-width="2" />
        <polygon points="${points}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'octagon') {
    const points = '10,3 22,3 29,10 29,22 22,29 10,29 3,22 3,10';
    if (!withOutline) return `<polygon points="${points}" fill="${c}" />`;
    return `
      <g>
        <polygon points="${points}" fill="none" stroke="#000000" stroke-width="4" />
        <polygon points="${points}" fill="none" stroke="#ffffff" stroke-width="2" />
        <polygon points="${points}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'pill') {
    if (!withOutline) return `<rect x="3.2" y="9" width="25.6" height="14" rx="7" ry="7" fill="${c}" />`;
    return `
      <g>
        <rect x="3.2" y="9" width="25.6" height="14" rx="7" ry="7" fill="none" stroke="#000000" stroke-width="4" />
        <rect x="3.2" y="9" width="25.6" height="14" rx="7" ry="7" fill="none" stroke="#ffffff" stroke-width="2" />
        <rect x="3.2" y="9" width="25.6" height="14" rx="7" ry="7" fill="${c}" />
      </g>
    `;
  }

  if (s === 'pin') {
    const d =
      'M16 30 C16 30 7 20.5 7 13.5 C7 8.3 11.1 4 16 4 C20.9 4 25 8.3 25 13.5 C25 20.5 16 30 16 30 Z';
    if (!withOutline) return `<path d="${d}" fill="${c}" />`;
    return `
      <g>
        <path d="${d}" fill="none" stroke="#000000" stroke-width="4" />
        <path d="${d}" fill="none" stroke="#ffffff" stroke-width="2" />
        <path d="${d}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'flag') {
    const d =
      'M8 4 H11 V6 C14 5 17 5 20 6 C23 7 25 7 26 6 V16 C24 17 21 17 18 16 C15 15 13 15 11 16 V30 H8 Z';
    if (!withOutline) return `<path d="${d}" fill="${c}" />`;
    return `
      <g>
        <path d="${d}" fill="none" stroke="#000000" stroke-width="4" />
        <path d="${d}" fill="none" stroke="#ffffff" stroke-width="2" />
        <path d="${d}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'crosshair') {
    const ringR = 12.5;
    const centerR = 2.2;
    const lineW = 3;
    const cap = 'round';

    if (!withOutline) {
      return `
        <g>
          <circle cx="16" cy="16" r="${ringR}" fill="none" stroke="${c}" stroke-width="${lineW}" />
          <line x1="16" y1="5.2" x2="16" y2="26.8" stroke="${c}" stroke-width="${lineW}" stroke-linecap="${cap}" />
          <line x1="5.2" y1="16" x2="26.8" y2="16" stroke="${c}" stroke-width="${lineW}" stroke-linecap="${cap}" />
          <circle cx="16" cy="16" r="${centerR}" fill="${c}" />
        </g>
      `;
    }

    return `
      <g>
        <circle cx="16" cy="16" r="${ringR}" fill="none" stroke="#000000" stroke-width="6" />
        <circle cx="16" cy="16" r="${ringR}" fill="none" stroke="#ffffff" stroke-width="3" />
        <circle cx="16" cy="16" r="${ringR}" fill="none" stroke="${c}" stroke-width="${lineW}" />

        <line x1="16" y1="5.2" x2="16" y2="26.8" stroke="#000000" stroke-width="6" stroke-linecap="${cap}" />
        <line x1="16" y1="5.2" x2="16" y2="26.8" stroke="#ffffff" stroke-width="3" stroke-linecap="${cap}" />
        <line x1="16" y1="5.2" x2="16" y2="26.8" stroke="${c}" stroke-width="${lineW}" stroke-linecap="${cap}" />

        <line x1="5.2" y1="16" x2="26.8" y2="16" stroke="#000000" stroke-width="6" stroke-linecap="${cap}" />
        <line x1="5.2" y1="16" x2="26.8" y2="16" stroke="#ffffff" stroke-width="3" stroke-linecap="${cap}" />
        <line x1="5.2" y1="16" x2="26.8" y2="16" stroke="${c}" stroke-width="${lineW}" stroke-linecap="${cap}" />

        <circle cx="16" cy="16" r="${centerR + 1.4}" fill="#000000" />
        <circle cx="16" cy="16" r="${centerR + 0.7}" fill="#ffffff" />
        <circle cx="16" cy="16" r="${centerR}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'shield') {
    const d = 'M16 3 L28 8 V18 C28 25 22.5 28.7 16 30 C9.5 28.7 4 25 4 18 V8 Z';
    if (!withOutline) return `<path d="${d}" fill="${c}" />`;
    return `
      <g>
        <path d="${d}" fill="none" stroke="#000000" stroke-width="4" />
        <path d="${d}" fill="none" stroke="#ffffff" stroke-width="2" />
        <path d="${d}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'badge') {
    if (!withOutline) return `<rect x="4" y="6" width="24" height="20" rx="8" ry="8" fill="${c}" />`;
    return `
      <g>
        <rect x="4" y="6" width="24" height="20" rx="8" ry="8" fill="none" stroke="#000000" stroke-width="4" />
        <rect x="4" y="6" width="24" height="20" rx="8" ry="8" fill="none" stroke="#ffffff" stroke-width="2" />
        <rect x="4" y="6" width="24" height="20" rx="8" ry="8" fill="${c}" />
      </g>
    `;
  }

  if (s === 'tag') {
    const d = 'M4 14 L14 4 H26 V16 L16 26 H4 Z';
    if (!withOutline) return `<path d="${d}" fill="${c}" />`;
    return `
      <g>
        <path d="${d}" fill="none" stroke="#000000" stroke-width="4" />
        <path d="${d}" fill="none" stroke="#ffffff" stroke-width="2" />
        <path d="${d}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'key') {
    const d =
      'M12.5 18.5 C9.2 18.5 6.5 15.8 6.5 12.5 C6.5 9.2 9.2 6.5 12.5 6.5 C15.6 6.5 18.1 8.8 18.5 11.8 H29.5 V15.5 H26.5 V18.5 H23.5 V21.5 H19.8 C18.7 20.0 16.8 18.5 12.5 18.5 Z';
    if (!withOutline) return `<path d="${d}" fill="${c}" />`;
    return `
      <g>
        <path d="${d}" fill="none" stroke="#000000" stroke-width="4" />
        <path d="${d}" fill="none" stroke="#ffffff" stroke-width="2" />
        <path d="${d}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'lock') {
    const d =
      'M10 14 V11 C10 7.7 12.7 5 16 5 C19.3 5 22 7.7 22 11 V14 H24 C25.1 14 26 14.9 26 16 V27 C26 28.1 25.1 29 24 29 H8 C6.9 29 6 28.1 6 27 V16 C6 14.9 6.9 14 8 14 Z M13 14 H19 V11 C19 9.3 17.7 8 16 8 C14.3 8 13 9.3 13 11 Z';
    if (!withOutline) return `<path d="${d}" fill="${c}" />`;
    return `
      <g>
        <path d="${d}" fill="none" stroke="#000000" stroke-width="4" />
        <path d="${d}" fill="none" stroke="#ffffff" stroke-width="2" />
        <path d="${d}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'unlock') {
    const dBody =
      'M10 14 H24 C25.1 14 26 14.9 26 16 V27 C26 28.1 25.1 29 24 29 H8 C6.9 29 6 28.1 6 27 V16 C6 14.9 6.9 14 8 14 H10 Z';
    const dShackle =
      'M12.5 14 V11 C12.5 7.7 15.2 5 18.5 5 C21.8 5 24.5 7.7 24.5 11 V12.2 H21.5 V11 C21.5 9.3 20.2 8 18.5 8 C16.8 8 15.5 9.3 15.5 11 V14 Z';

    if (!withOutline) {
      return `<g><path d="${dBody}" fill="${c}" /><path d="${dShackle}" fill="${c}" /></g>`;
    }

    return `
      <g>
        <path d="${dBody}" fill="none" stroke="#000000" stroke-width="4" />
        <path d="${dBody}" fill="none" stroke="#ffffff" stroke-width="2" />
        <path d="${dBody}" fill="${c}" />

        <path d="${dShackle}" fill="none" stroke="#000000" stroke-width="4" />
        <path d="${dShackle}" fill="none" stroke="#ffffff" stroke-width="2" />
        <path d="${dShackle}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'info') {
    const textFill = pickBlackOrWhiteText(c);
    const ringR = 13.2;
    const dotR = 2.0;
    const dotY = 10.0;
    const barX = 14.8;
    const barY = 13.5;
    const barW = 2.4;
    const barH = 11.8;

    if (!withOutline) {
      return `
        <g>
          <circle cx="16" cy="16" r="${ringR}" fill="${c}" />
          <circle cx="16" cy="${dotY}" r="${dotR}" fill="${textFill}" />
          <rect x="${barX}" y="${barY}" width="${barW}" height="${barH}" rx="1.1" ry="1.1" fill="${textFill}" />
        </g>
      `;
    }

    return `
      <g>
        <circle cx="16" cy="16" r="${ringR}" fill="none" stroke="#000000" stroke-width="4" />
        <circle cx="16" cy="16" r="${ringR}" fill="none" stroke="#ffffff" stroke-width="2" />
        <circle cx="16" cy="16" r="${ringR - 2.3}" fill="${c}" />
        <circle cx="16" cy="${dotY}" r="${dotR}" fill="${textFill}" />
        <rect x="${barX}" y="${barY}" width="${barW}" height="${barH}" rx="1.1" ry="1.1" fill="${textFill}" />
      </g>
    `;
  }

  if (s === 'plus') {
    const d = 'M14 5 H18 V14 H27 V18 H18 V27 H14 V18 H5 V14 H14 Z';
    if (!withOutline) return `<path d="${d}" fill="${c}" />`;
    return `
      <g>
        <path d="${d}" fill="none" stroke="#000000" stroke-width="4" />
        <path d="${d}" fill="none" stroke="#ffffff" stroke-width="2" />
        <path d="${d}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'bolt') {
    const points = '18,2 6,18 14,18 12,30 26,12 18,12';
    if (!withOutline) return `<polygon points="${points}" fill="${c}" />`;
    return `
      <g>
        <polygon points="${points}" fill="none" stroke="#000000" stroke-width="4" />
        <polygon points="${points}" fill="none" stroke="#ffffff" stroke-width="2" />
        <polygon points="${points}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'drop') {
    const d = 'M16 3 C16 3 7 13 7 19.2 C7 24.8 11 29.2 16 29.2 C21 29.2 25 24.8 25 19.2 C25 13 16 3 16 3 Z';
    if (!withOutline) return `<path d="${d}" fill="${c}" />`;
    return `
      <g>
        <path d="${d}" fill="none" stroke="#000000" stroke-width="4" />
        <path d="${d}" fill="none" stroke="#ffffff" stroke-width="2" />
        <path d="${d}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'wrench') {
    const d =
      'M20.8 4.2 C18.6 4.0 16.5 5.0 15.1 6.8 L18.0 9.7 L15.7 12.0 L12.8 9.1 C11.0 10.6 10.0 12.8 10.2 15.0 C10.3 16.1 10.7 17.1 11.3 18.0 L4.6 24.7 C3.8 25.5 3.8 26.8 4.6 27.6 L4.4 27.8 C5.2 28.6 6.5 28.6 7.3 27.8 L14.0 21.1 C14.9 21.7 15.9 22.1 17.0 22.2 C19.2 22.4 21.4 21.4 22.9 19.6 L20.0 16.7 L22.3 14.4 L25.2 17.3 C27.0 15.8 28.0 13.6 27.8 11.4 C27.6 8.0 24.2 4.6 20.8 4.2 Z';
    if (!withOutline) return `<path d="${d}" fill="${c}" />`;
    return `
      <g>
        <path d="${d}" fill="none" stroke="#000000" stroke-width="4" />
        <path d="${d}" fill="none" stroke="#ffffff" stroke-width="2" />
        <path d="${d}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'cone') {
    const points = '16 4 27 26 5 26';
    const base = 'M4 26 H28 V29 H4 Z';
    if (!withOutline) return `<g><polygon points="${points}" fill="${c}" /><path d="${base}" fill="${c}" /></g>`;
    return `
      <g>
        <polygon points="${points}" fill="none" stroke="#000000" stroke-width="4" />
        <polygon points="${points}" fill="none" stroke="#ffffff" stroke-width="2" />
        <polygon points="${points}" fill="${c}" />
        <path d="${base}" fill="none" stroke="#000000" stroke-width="4" />
        <path d="${base}" fill="none" stroke="#ffffff" stroke-width="2" />
        <path d="${base}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'star') {
    const points =
      '16,3.2 19.6,11.6 28.6,12.4 21.8,18.2 24.0,26.8 16,22.2 8.0,26.8 10.2,18.2 3.4,12.4 12.4,11.6';
    if (!withOutline) return `<polygon points="${points}" fill="${c}" />`;
    return `
      <g>
        <polygon points="${points}" fill="none" stroke="#000000" stroke-width="4" />
        <polygon points="${points}" fill="none" stroke="#ffffff" stroke-width="2" />
        <polygon points="${points}" fill="${c}" />
      </g>
    `;
  }

  if (s === 'heart') {
    const d =
      'M16 29.2 C16 29.2 3.2 22.4 3.2 13.4 C3.2 8.8 6.8 5.2 11.4 5.2 C13.9 5.2 16.1 6.4 17.4 8.2 C18.7 6.4 20.9 5.2 23.4 5.2 C28.0 5.2 31.6 8.8 31.6 13.4 C31.6 22.4 16 29.2 16 29.2 Z';
    if (!withOutline) return `<path d="${d}" fill="${c}" />`;
    return `
      <g>
        <path d="${d}" fill="none" stroke="#000000" stroke-width="4" />
        <path d="${d}" fill="none" stroke="#ffffff" stroke-width="2" />
        <path d="${d}" fill="${c}" />
      </g>
    `;
  }

  if (!withOutline) return `<circle cx="16" cy="16" r="14" fill="${c}" />`;

  return `
    <g>
      <circle cx="16" cy="16" r="14" fill="none" stroke="#000000" stroke-width="4" />
      <circle cx="16" cy="16" r="14" fill="none" stroke="#ffffff" stroke-width="2" />
      <circle cx="16" cy="16" r="12" fill="${c}" />
    </g>
  `;
}

function getTinyShapeSvg(shape, fill, opacity, pxSize) {
  const s = normalizeMarkerShape(shape) || 'circle';
  const c = normalizeHex(fill);
  const o = Number.isFinite(Number(opacity)) ? Number(opacity) : 1;

  const shapeNode = getShapeNodeSvg(s, c, false);
  return `<svg width="${pxSize}" height="${pxSize}" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" style="display:block;opacity:${o};">${shapeNode}</svg>`;
}

function getNumberBadgeSvg(shape, numberValue, fill, opacity, size = 12) {
  const s = normalizeMarkerShape(shape) || 'circle';
  const rawN = normalizeBadgeNumberValue(numberValue);
  const displayText = numberValueToBadgeText(rawN);

  const c = normalizeHex(fill);
  const o = Number.isFinite(Number(opacity)) ? Number(opacity) : 1;
  const textFill = pickBlackOrWhiteText(c);

  const isLetter = /^[A-Z]$/.test(displayText);
  const fontSize = isLetter ? 13 : rawN >= 10 && rawN <= 99 ? 12 : 13;

  const commonSvgOpen = `<svg width="${size}" height="${size}" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" style="display:block;opacity:${o};">`;
  const commonSvgClose = `</svg>`;

  let shapeNode = getShapeNodeSvg(s, c, false);
  let textY = 16;

  if (s === 'triangle') textY = 18;
  if (s === 'pin') textY = 14.5;
  if (s === 'cone') textY = 18;
  if (s === 'tag') textY = 16.5;
  if (s === 'flag') textY = 16.5;

  const textNode = `
    <text x="16" y="${textY}" text-anchor="middle" dominant-baseline="middle"
      font-family="system-ui,-apple-system,Segoe UI,Roboto,Arial,sans-serif"
      font-weight="900" font-size="${fontSize}" fill="${textFill}"
      style="paint-order:stroke;stroke:rgba(0,0,0,0.45);stroke-width:2px;">
      ${displayText}
    </text>
  `;

  return `${commonSvgOpen}${shapeNode}${textNode}${commonSvgClose}`;
}

function getNumberBadgeHTML(shape, numberValue, colour, iconOpacity, size = 12) {
  const svg = getNumberBadgeSvg(shape, numberValue, colour, iconOpacity, size);
  return `<div style="width:${size}px;height:${size}px;display:block;">${svg}</div>`;
}

export default function POIMarker({
  poi,
  customCategories = [],
  customTypes = [],
  shouldFlash = false,
  typeColours = {},
  showLabel = false,
  found = false,
  onToggleFound = () => {},
  opacity = 1,
  labelOpacity = 1,
  iconOpacity = 1,
  onMouseEnter,
  onMouseLeave,
  mapId,
  userId,
  mapOwnerId,
  onCategoryUpdate,
  modalOpen,
  isEditing,
  onOpenModal,
  onCloseModal,
  onStartEdit,
  onStopEdit,
  canEdit = false,
  overlayOptions = [],
  overlayIdDefault = null,
  ...rest
}) {
  const markerRef = useRef();
  const map = useMap();

  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const thisPoiId = useMemo(() => {
    const v = poi && (poi.id ?? poi.poi_id ?? poi.poiId ?? poi.ID ?? null);
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [poi]);

  const [movePoiOpen, setMovePoiOpen] = useState(false);
  const [movePoiTarget, setMovePoiTarget] = useState(null);
  const [moveSaving, setMoveSaving] = useState(false);
  const [moveActive, setMoveActive] = useState(false);

  const [tempPos, setTempPos] = useState(null);
  const [committedPos, setCommittedPos] = useState(null);
  const [origPos, setOrigPos] = useState(null);

  const basePos = useMemo(() => {
    const y = Number(poi?.y);
    const x = Number(poi?.x);
    if (Number.isFinite(y) && Number.isFinite(x)) return [y, x];
    return null;
  }, [poi?.x, poi?.y]);

  const effectiveMarkerPos = useMemo(() => {
    if (moveActive && Array.isArray(tempPos) && tempPos.length === 2) return tempPos;
    if (!moveActive && Array.isArray(committedPos) && committedPos.length === 2) return committedPos;
    return basePos;
  }, [moveActive, tempPos, committedPos, basePos]);

  useEffect(() => {
    const onStart = (e) => {
      const p = e?.detail?.poi || null;
      if (!p) return;

      const v = p && (p.id ?? p.poi_id ?? p.poiId ?? p.ID ?? null);
      const n = Number(v);
      const targetId = Number.isFinite(n) && n > 0 ? n : null;

      if (!targetId || !thisPoiId) return;
      if (String(targetId) !== String(thisPoiId)) return;

      if (!effectiveMarkerPos) return;

      const startPos = effectiveMarkerPos;

      setMovePoiTarget(p);
      setMovePoiOpen(true);
      setMoveSaving(false);

      setOrigPos(startPos);
      setTempPos(startPos);
      setMoveActive(true);
    };

    const onCancel = (e) => {
      const detailPoiId = e?.detail?.poiId ?? null;
      if (detailPoiId != null && thisPoiId != null && String(detailPoiId) !== String(thisPoiId)) return;

      if (moveSaving) return;

      if (Array.isArray(origPos) && origPos.length === 2) {
        setTempPos(origPos);
      }
      setMoveActive(false);
      setMovePoiOpen(false);
      setMovePoiTarget(null);
      setMoveSaving(false);
      setOrigPos(null);
      setTempPos(null);
    };

    try {
      window.addEventListener('pa-poi-move-start', onStart);
      window.addEventListener('pa-poi-move-cancel', onCancel);
    } catch {}

    return () => {
      try {
        window.removeEventListener('pa-poi-move-start', onStart);
        window.removeEventListener('pa-poi-move-cancel', onCancel);
      } catch {}
    };
  }, [thisPoiId, effectiveMarkerPos, origPos, moveSaving]);

  const typeObj =
    customTypes.find((t) => String(t.id) === String(poi.type_id) || String(t.id) === String(poi.type)) ||
    (typeof poi.type === 'object' ? poi.type : {});
  const categoryObj =
    customCategories.find(
      (c) =>
        String(c.id) === String(poi.category_id) ||
        String(c.id) === String(typeObj.category_id) ||
        String(c.id) === String(poi.category)
    ) || (typeof poi.category === 'object' ? poi.category : {});

  const resolvedTypeName = typeObj?.name || (typeof poi.type === 'string' ? poi.type : '');
  const resolvedCategoryName = categoryObj?.name || (typeof poi.category === 'string' ? poi.category : '');

  const typeIconCandidate = normalizeMarkerShape(typeObj?.icon) || normalizeMarkerShape(typeObj?.shape);
  const typeHasLegacyOverride = !!typeIconCandidate && typeIconCandidate !== 'circle';

  const poiShapeCandidate =
    normalizeMarkerShape(poi.marker_shape) ||
    normalizeMarkerShape(poi.markerShape) ||
    normalizeMarkerShape(poi.badge_shape) ||
    normalizeMarkerShape(poi.badgeShape) ||
    normalizeMarkerShape(poi.shape);

  const resolvedMarkerShape = (typeHasLegacyOverride ? typeIconCandidate : poiShapeCandidate) || 'circle';

  const resolvedNumberValueRaw =
    poi.number_value != null
      ? poi.number_value
      : poi.numberValue != null
      ? poi.numberValue
      : poi.badge_number != null
      ? poi.badge_number
      : poi.badgeNumber != null
      ? poi.badgeNumber
      : 1;

  const resolvedNumberValue = normalizeBadgeNumberValue(resolvedNumberValueRaw);
  const resolvedNumberDisplay = numberValueToBadgeText(resolvedNumberValue);

  const rawTypeColour =
    typeObj?.color != null
      ? typeObj.color
      : typeObj?.colour != null
      ? typeObj.colour
      : poi?.colour != null
      ? poi.colour
      : poi?.color != null
      ? poi.color
      : null;

  const rawOverrideColour =
    poi?.colour_override != null
      ? poi.colour_override
      : poi?.color_override != null
      ? poi.color_override
      : poi?.colourOverride != null
      ? poi.colourOverride
      : poi?.colorOverride != null
      ? poi.colorOverride
      : null;

  const resolvedTypeColour = normalizeHex(
    rawTypeColour != null
      ? rawTypeColour
      : typeColours[resolvedTypeName] != null
      ? typeColours[resolvedTypeName]
      : '#ff0000'
  );

  const resolvedOverrideColour =
    rawOverrideColour != null && String(rawOverrideColour).trim() !== '' ? normalizeHex(rawOverrideColour) : null;

  const allowIndividualColours =
    toBool01(poi?.poi_allow_individual_colours) ||
    toBool01(poi?.allow_individual_colours) ||
    toBool01(poi?.allowIndividualColours) ||
    toBool01(poi?.poiAllowIndividualColours) ||
    (resolvedOverrideColour ? true : false);

  const resolvedColour = allowIndividualColours && resolvedOverrideColour ? resolvedOverrideColour : resolvedTypeColour;

  const resolvedPoiName = poi.poi_name || poi.name || '';
  const labelText = resolvedPoiName || resolvedTypeName || (poi.type ? `${poi.type}` : '<i>(No name)</i>');

  const visualStyleRaw =
    poi?.visual_style != null
      ? poi.visual_style
      : poi?.visualStyle != null
      ? poi.visualStyle
      : poi?.visual != null
      ? poi.visual
      : 'shape_text';

  const visualStyle = normalizeVisualStyle(visualStyleRaw);

  const styleShapeOnly = isShapeOnly(visualStyle);
  const styleShapeText = isShapeText(visualStyle);
  const styleAuto = isAuto(visualStyle);
  const styleNumber = isNumber(visualStyle);
  const styleNumberLabel = isNumberLabel(visualStyle);
  const styleIconOnly = isIconOnly(visualStyle);
  const styleTextOnly = isTextOnly(visualStyle);
  const styleIconText = isIconText(visualStyle);
  const styleIconLabel = isIconLabel(visualStyle);
  const styleIconLabelAbove = isIconLabelAbove(visualStyle);

  const styleIsLabelIcon = styleIconOnly || styleTextOnly || styleIconText;
  const forceHideLabel = styleShapeOnly;

  const hideSecondaryLabels = !showLabel;
  const effectivePrimaryLabelOpacity = hideSecondaryLabels && styleIsLabelIcon ? 1 : labelOpacity;

  function getShapeHTML() {
    const px = 19;
    const svg = getTinyShapeSvg(resolvedMarkerShape, resolvedColour, iconOpacity, px);
    return `<div style="width:${px}px;height:${px}px;display:block;line-height:0;">${svg}</div>`;
  }

  const labelBorder = typeof poi.label_border === 'undefined' || poi.label_border === null ? 1 : Number(poi.label_border);

  const rawLabelBgColour =
    poi.label_bg_colour != null ? poi.label_bg_colour : typeObj.label_bg_colour != null ? typeObj.label_bg_colour : '#000';

  const labelBgColour = rawLabelBgColour;

  const rawPoiIconUrl =
    poi && typeof poi.icon_url === 'string' && poi.icon_url.trim()
      ? poi.icon_url.trim()
      : poi && typeof poi.icon_file === 'string' && poi.icon_file.trim()
      ? poi.icon_file.trim()
      : '';
  const rawTypeIconUrl =
    typeObj && typeof typeObj.icon_url === 'string' && typeObj.icon_url.trim() ? typeObj.icon_url.trim() : '';

  const resolvedPoiIconUrl = isLikelyImageUrl(rawPoiIconUrl) ? resolveUploadsUrl(rawPoiIconUrl) : '';
  const resolvedTypeIconUrl = isLikelyImageUrl(rawTypeIconUrl) ? resolveUploadsUrl(rawTypeIconUrl) : '';

  const labelIconUrl = resolvedPoiIconUrl || resolvedTypeIconUrl;

  const labelHtmlBase = styleIconText
    ? labelIconUrl
      ? getIconWithLabelHTML(
          labelIconUrl,
          labelText,
          resolvedColour,
          64,
          labelBorder,
          labelBgColour,
          effectivePrimaryLabelOpacity
        )
      : getLabelHTML(labelText, resolvedColour, labelBorder, labelBgColour, effectivePrimaryLabelOpacity, false)
    : styleIconOnly
    ? labelIconUrl
      ? getColouredIconHTML(labelIconUrl, resolvedColour, 64, labelBorder, labelBgColour, effectivePrimaryLabelOpacity)
      : getLabelHTML(labelText, resolvedColour, labelBorder, labelBgColour, effectivePrimaryLabelOpacity, false)
    : styleTextOnly
    ? getLabelHTML(labelText, resolvedColour, labelBorder, labelBgColour, effectivePrimaryLabelOpacity, false)
    : labelIconUrl
    ? getColouredIconHTML(labelIconUrl, resolvedColour, 64, labelBorder, labelBgColour, effectivePrimaryLabelOpacity)
    : getLabelHTML(labelText, resolvedColour, labelBorder, labelBgColour, effectivePrimaryLabelOpacity, false);

  const labelHtmlForIcon = useMemo(() => {
    const s = String(labelHtmlBase || '');
    return s
      .replace(/transform-origin:\s*bottom\s+center\s*;/gi, 'transform-origin:center;')
      .replace(/transform-origin:\s*bottom\s+center\s*"/gi, 'transform-origin:center;"');
  }, [labelHtmlBase]);

  const labelHtmlCenteredForIcon = useMemo(() => {
    const base = String(labelHtmlForIcon || '');

    return `
      <div style="position:relative;transform:translate(-50%,-50%);transform-origin:50% 50%;pointer-events:auto;">
        <div class="${shouldFlash ? 'flash' : ''}" style="display:inline-block;transform-origin:50% 50%;transform-box:fill-box;">
          ${base}
        </div>
      </div>
    `;
  }, [labelHtmlForIcon, shouldFlash]);

  const iconLabelMarkerSize = 32;

  const iconLabelMarkerHtml = useMemo(() => {
    if (labelIconUrl) {
      return `
        <div style="width:${iconLabelMarkerSize}px;height:${iconLabelMarkerSize}px;display:flex;align-items:center;justify-content:center;pointer-events:auto;line-height:0;">
          <div class="${shouldFlash ? 'flash' : ''}" style="width:${iconLabelMarkerSize}px;height:${iconLabelMarkerSize}px;display:flex;align-items:center;justify-content:center;pointer-events:none;">
            <img src="${labelIconUrl}" style="width:${iconLabelMarkerSize}px;height:${iconLabelMarkerSize}px;object-fit:contain;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.22));opacity:${iconOpacity};" />
          </div>
        </div>
      `;
    }

    const px = 19;
    const svg = getTinyShapeSvg(resolvedMarkerShape, resolvedColour, iconOpacity, px);
    return `
      <div style="width:${iconLabelMarkerSize}px;height:${iconLabelMarkerSize}px;display:flex;align-items:center;justify-content:center;pointer-events:auto;line-height:0;">
        <div class="${shouldFlash ? 'flash' : ''}" style="pointer-events:none;">
          <div style="width:${px}px;height:${px}px;display:block;line-height:0;">${svg}</div>
        </div>
      </div>
    `;
  }, [labelIconUrl, iconLabelMarkerSize, shouldFlash, iconOpacity, resolvedMarkerShape, resolvedColour]);

  const hoverLabelHtml = useMemo(() => {
    return getLabelHTML(labelText, resolvedColour, labelBorder, labelBgColour, labelOpacity, true);
  }, [labelText, resolvedColour, labelBorder, labelBgColour, labelOpacity]);

  const icon = useMemo(() => {
    if (styleIconLabel) {
      return L.divIcon({
        className: 'poi-marker poi-marker-icon-hoverlabel',
        html: iconLabelMarkerHtml,
        iconSize: [iconLabelMarkerSize, iconLabelMarkerSize],
        iconAnchor: [Math.round(iconLabelMarkerSize / 2), Math.round(iconLabelMarkerSize / 2)],
      });
    }

    if (styleIconLabelAbove) {
      return L.divIcon({
        className: 'poi-marker poi-marker-icon-labelabove',
        html: iconLabelMarkerHtml,
        iconSize: [iconLabelMarkerSize, iconLabelMarkerSize],
        iconAnchor: [Math.round(iconLabelMarkerSize / 2), Math.round(iconLabelMarkerSize / 2)],
      });
    }

    if (styleIsLabelIcon) {
      const s = iconLabelMarkerSize;
      return L.divIcon({
        className: 'poi-marker poi-marker-label-icon',
        html: labelHtmlCenteredForIcon,
        iconSize: [s, s],
        iconAnchor: [Math.round(s / 2), Math.round(s / 2)],
      });
    }

    if (styleNumber || styleNumberLabel) {
      const size = 23;
      const html = `<div class="${shouldFlash ? 'flash' : ''}" style="width:${size}px;height:${size}px;display:block;line-height:0;">
        ${getNumberBadgeHTML(resolvedMarkerShape, resolvedNumberValue, resolvedColour, iconOpacity, size)}
      </div>`;

      return L.divIcon({
        className: styleNumberLabel
          ? 'poi-marker poi-marker-number poi-marker-number-hoverlabel'
          : 'poi-marker poi-marker-number',
        html,
        iconSize: [size, size],
        iconAnchor: [Math.round(size / 2), Math.round(size / 2)],
      });
    }

    const iconSize = 19;
    return L.divIcon({
      className: 'poi-marker',
      html: `<div class="${shouldFlash ? 'flash' : ''}">${getShapeHTML()}</div>`,
      iconSize: [iconSize, iconSize],
      iconAnchor: [Math.round(iconSize / 2), Math.round(iconSize / 2)],
    });
  }, [
    styleIconLabel,
    styleIconLabelAbove,
    iconLabelMarkerHtml,
    iconLabelMarkerSize,
    styleIsLabelIcon,
    labelHtmlCenteredForIcon,
    shouldFlash,
    resolvedMarkerShape,
    resolvedNumberValue,
    resolvedColour,
    iconOpacity,
    styleNumber,
    styleNumberLabel,
  ]);

  const handleMarkerClick = () => {
    if (moveActive) return;
    if (modalOpen) return;
    if (typeof onOpenModal === 'function') onOpenModal(poi.id);
  };

  const handleCloseModal = () => {
    setImageViewerOpen(false);
    if (typeof onStopEdit === 'function') onStopEdit();
    if (typeof onCloseModal === 'function') onCloseModal();
  };

  const handleEditClick = () => {
    if (!canEdit) return;
    if (typeof onStartEdit === 'function') onStartEdit();
  };

  const panelWidth = 400;

  const modalBackdropStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.3)',
    zIndex: 2000,
    cursor: 'default',
  };

  const centerModalStyle = {
    position: 'fixed',
    left: '50%',
    top: '10%',
    transform: 'translate(-50%,0)',
    width: panelWidth,
    background: '#232427',
    borderRadius: 16,
    boxShadow: '0 6px 24px #000b',
    padding: 12,
    color: '#fff',
    minHeight: 180,
    border: '1.5px solid #23252a',
    zIndex: 2100,
  };

  const centerModalEditStyle = {
    ...centerModalStyle,
    width: 'min(860px, 96vw)',
    maxWidth: '96vw',
    height: 'auto',
    maxHeight: '90vh',
    display: 'block',
    overflow: 'visible',
  };

  const rawPoiImageUrl = poi?.image_url && typeof poi.image_url === 'string' ? poi.image_url : '';
  const poiImageUrl = rawPoiImageUrl ? resolveUploadsUrl(rawPoiImageUrl) : '';

  const tooltipDirection = 'top';
  const tooltipOffset = [0, -10];

  const tooltipHtml = styleIconLabel || styleNumberLabel || styleIconLabelAbove ? hoverLabelHtml : labelHtmlBase;

  const hoverStyleActive = (styleAuto || styleIconLabel || styleNumberLabel) && !!isHovering;

  const tooltipShouldRender =
    !forceHideLabel &&
    ((styleShapeText && showLabel) ||
      (styleNumber && showLabel) ||
      (styleAuto && hoverStyleActive) ||
      (styleIconLabel && hoverStyleActive) ||
      (styleNumberLabel && hoverStyleActive) ||
      (styleIconLabelAbove && showLabel));

  const performMoveSave = async () => {
    if (moveSaving) return;
    if (!thisPoiId) return;

    const mapIdNum = Number(mapId || 0) || 0;
    if (!mapIdNum) {
      alert('Missing mapId in POIMarker; cannot save POI position.');
      return;
    }

    let ll = null;
    try {
      if (markerRef.current && markerRef.current.getLatLng) {
        ll = markerRef.current.getLatLng();
      } else if (markerRef.current && markerRef.current.leafletElement && markerRef.current.leafletElement.getLatLng) {
        ll = markerRef.current.leafletElement.getLatLng();
      }
    } catch {
      ll = null;
    }

    const lat = Number(ll?.lat);
    const lng = Number(ll?.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      alert('Could not read marker position to save.');
      return;
    }

    setMoveSaving(true);
    try {
      const res = await fetch(apiUrl(`/api/poi/update-position/${thisPoiId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          map_id: mapIdNum,
          x: lng,
          y: lat,
        }),
      });

      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok) {
        throw new Error(data?.error || 'Failed to save POI position.');
      }

      setCommittedPos([lat, lng]);
      setMoveActive(false);
      setMovePoiOpen(false);
      setMovePoiTarget(null);
      setMoveSaving(false);
      setOrigPos(null);
      setTempPos(null);

      try {
        window.dispatchEvent(
          new CustomEvent('pa-poi-position-saved', {
            detail: { poiId: thisPoiId, poi: movePoiTarget || poi, x: lng, y: lat, lat, lng },
          })
        );
      } catch {}
    } catch (err) {
      setMoveSaving(false);
      alert(err?.message || 'Failed to save POI position.');
    }
  };

  const updateDraftFromMarker = useCallback(
    (e) => {
      if (!moveActive) return;
      if (moveSaving) return;

      try {
        const ll = e?.target?.getLatLng ? e.target.getLatLng() : null;
        if (!ll) return;
        const lat = Number(ll.lat);
        const lng = Number(ll.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        setTempPos([lat, lng]);
      } catch {}
    },
    [moveActive, moveSaving]
  );

  if (!effectiveMarkerPos) return null;

  return (
    <>
      <MovePOIModal
        open={movePoiOpen}
        poi={movePoiTarget}
        saving={moveSaving}
        onCancel={() => {
          if (moveSaving) return;

          if (Array.isArray(origPos) && origPos.length === 2) {
            setTempPos(origPos);
            setCommittedPos(origPos);
          }

          setMoveActive(false);
          setMovePoiOpen(false);
          setMovePoiTarget(null);
          setMoveSaving(false);
          setOrigPos(null);
          setTempPos(null);
        }}
        onSave={() => {
          performMoveSave();
        }}
      />

      <Marker
        position={effectiveMarkerPos}
        icon={icon}
        ref={markerRef}
        draggable={!!moveActive && !moveSaving}
        eventHandlers={{
          click: handleMarkerClick,
          mouseover: () => {
            setIsHovering(true);
            onMouseEnter && onMouseEnter(poi.id);
          },
          mouseout: () => {
            setIsHovering(false);
            onMouseLeave && onMouseLeave(null);
          },
          drag: updateDraftFromMarker,
          dragend: updateDraftFromMarker,
        }}
        opacity={opacity}
        zIndexOffset={found ? -500 : 0}
        {...rest}
      >
        {tooltipShouldRender && (
          <Tooltip
            direction={tooltipDirection}
            offset={tooltipOffset}
            opacity={labelOpacity}
            permanent
            interactive={false}
            className="poi-label-tooltip"
          >
            <span
              style={{
                opacity: labelOpacity,
                pointerEvents: 'none',
              }}
              dangerouslySetInnerHTML={{ __html: tooltipHtml }}
            />
          </Tooltip>
        )}
      </Marker>

      {modalOpen && !isEditing && (
        <>
          <div
            className="poi-modal-backdrop"
            style={modalBackdropStyle}
            onClick={(e) => {
              e.stopPropagation();
              handleCloseModal();
            }}
            role="presentation"
          />
          <div
            className="poi-info-modal"
            style={centerModalStyle}
            onClick={(e) => {
              e.stopPropagation();
            }}
            role="dialog"
            aria-modal="true"
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCloseModal();
              }}
              style={{
                position: 'absolute',
                top: 8,
                right: 10,
                background: '#444',
                color: '#fff',
                border: 'none',
                borderRadius: 7,
                padding: '4px 9px',
                cursor: 'pointer',
              }}
            >
              ✕
            </button>

            <div style={{ marginBottom: 5 }}>
              <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 7 }}>
                Name:{' '}
                <span style={{ fontStyle: 'italic', fontWeight: 400 }}>
                  {poi.poi_name || poi.name || '(Unnamed)'}
                </span>
              </div>

              <div>
                <b>Category:</b> {resolvedCategoryName || ''}
              </div>
              <div>
                <b>Type Name:</b> {resolvedTypeName || ''}
              </div>

              <div>
                <b>Visual Style:</b> {String(visualStyle || 'shape_text')}
              </div>

              {(styleNumber || styleNumberLabel) && (
                <>
                  <div>
                    <b>Number Value:</b> {String(resolvedNumberDisplay)}
                  </div>
                  <div>
                    <b>Marker Shape:</b> {String(resolvedMarkerShape)}
                  </div>
                </>
              )}

              <div>
                <b>Type Colour:</b>{' '}
                <span
                  style={{
                    background: resolvedTypeColour,
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    border: '2px solid #444',
                    marginLeft: 4,
                  }}
                />
              </div>

              <div>
                <b>POI Colour Enabled:</b> {allowIndividualColours ? 'Yes' : 'No'}
              </div>

              <div>
                <b>POI Colour Override:</b>{' '}
                {resolvedOverrideColour ? (
                  <span
                    style={{
                      background: resolvedOverrideColour,
                      width: 18,
                      height: 18,
                      borderRadius: 4,
                      display: 'inline-block',
                      verticalAlign: 'middle',
                      border: '2px solid #444',
                      marginLeft: 4,
                    }}
                  />
                ) : (
                  <span style={{ color: '#888', fontStyle: 'italic' }}>None</span>
                )}
              </div>

              <div>
                <b>Rendered Colour:</b>{' '}
                <span
                  style={{
                    background: resolvedColour,
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    border: '2px solid #444',
                    marginLeft: 4,
                  }}
                />
              </div>

              <div>
                <b>Marker Shape:</b> {resolvedMarkerShape}
              </div>

              <div>
                <b>Label Border:</b> {labelBorder === 1 ? 'Yes' : 'No'}
              </div>

              <div>
                <b>Label Background Colour:</b>{' '}
                <span
                  style={{
                    background:
                      labelBgColour &&
                      (labelBgColour.toString().toLowerCase() === 'transparent' ||
                        labelBgColour.toString().toLowerCase() === 'none')
                        ? 'transparent'
                        : labelBgColour,
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    border: '2px solid #444',
                    marginLeft: 4,
                  }}
                />
              </div>

              <div style={{ marginTop: 8 }}>
                <b>POI Image:</b>{' '}
                {poiImageUrl ? (
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setImageViewerOpen(true);
                      }}
                      style={{
                        background: '#111',
                        border: '1px solid #3a3a3a',
                        padding: 0,
                        borderRadius: 10,
                        cursor: 'pointer',
                        width: 86,
                        height: 62,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title="Click to view"
                    >
                      <img
                        src={poiImageUrl}
                        alt="POI"
                        style={{
                          display: 'block',
                          maxWidth: '100%',
                          maxHeight: '100%',
                          width: 'auto',
                          height: 'auto',
                          objectFit: 'contain',
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </button>
                    <div style={{ fontSize: 12, color: '#9aa0a6', lineHeight: 1.2, paddingTop: 2 }}>
                      Click to enlarge
                    </div>
                  </div>
                ) : (
                  <span style={{ color: '#888', fontStyle: 'italic' }}>None</span>
                )}
              </div>

              <div style={{ marginTop: 8 }}>
                <b>Description:</b>{' '}
                {poi.description ? (
                  <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                    {renderTextWithLinks(poi.description)}
                  </span>
                ) : (
                  <span style={{ fontStyle: 'italic', color: '#999' }}>(No description)</span>
                )}
              </div>

              <div style={{ marginTop: 8 }}>
                <b>Type Icon:</b>{' '}
                {resolvedTypeIconUrl ? (
                  <img
                    src={resolvedTypeIconUrl}
                    alt="icon"
                    style={{
                      width: 22,
                      height: 22,
                      verticalAlign: 'middle',
                      borderRadius: 3,
                      border: '1px solid #222',
                      marginLeft: 6,
                    }}
                  />
                ) : (
                  <span style={{ color: '#888', fontStyle: 'italic' }}>None</span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
              {canEdit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick();
                  }}
                  style={{
                    background: '#444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 7,
                    padding: '7px 22px',
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Edit
                </button>
              )}

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFound(poi.id);
                }}
                style={{
                  background: found ? '#1ec656' : '#444',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 7,
                  padding: '7px 22px',
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {found ? 'Mark POI as Not Found' : 'Mark POI as Found'}
              </button>
            </div>
          </div>

          {imageViewerOpen && poiImageUrl && (
            <>
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  backgroundColor: 'rgba(0,0,0,0.72)',
                  zIndex: 2600,
                  cursor: 'zoom-out',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setImageViewerOpen(false);
                }}
                role="presentation"
              />
              <div
                style={{
                  position: 'fixed',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%,-50%)',
                  width: 'min(92vw, 980px)',
                  height: 'min(86vh, 720px)',
                  background: '#111214',
                  borderRadius: 14,
                  boxShadow: '0 10px 36px #000d',
                  border: '1px solid #2a2b2f',
                  zIndex: 2700,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  cursor: 'default',
                }}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    borderBottom: '1px solid #2a2b2f',
                    color: '#e2e4e9',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: 14,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {poi.poi_name || poi.name || 'POI Image'}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setImageViewerOpen(false);
                    }}
                    style={{
                      background: '#2f3136',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 8,
                      padding: '6px 10px',
                      cursor: 'pointer',
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div
                  style={{
                    flex: 1,
                    padding: 12,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={poiImageUrl}
                    alt="POI"
                    style={{
                      display: 'block',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      background: '#0f1012',
                      borderRadius: 12,
                      border: '1px solid #2a2b2f',
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            </>
          )}
        </>
      )}

      {modalOpen && isEditing && (
        <>
          <div
            className="poi-modal-backdrop"
            style={modalBackdropStyle}
            onClick={(e) => {
              e.stopPropagation();
              handleCloseModal();
            }}
            role="presentation"
          />
          <div
            className="poi-edit-modal"
            style={centerModalEditStyle}
            onClick={(e) => {
              e.stopPropagation();
            }}
            role="dialog"
            aria-modal="true"
          >
            <EditPOI
              poi={poi}
              customCategories={customCategories}
              customTypes={customTypes}
              mapId={mapId}
              userId={userId}
              mapOwnerId={mapOwnerId}
              onCategoryUpdate={onCategoryUpdate}
              canEdit={canEdit}
              overlayOptions={overlayOptions}
              overlayIdDefault={overlayIdDefault}
              onClose={() => {
                onStopEdit && onStopEdit();
                onCloseModal && onCloseModal();
              }}
            />
          </div>
        </>
      )}
    </>
  );
}
